import { Component, OnInit, OnDestroy } from "@angular/core";
import {
  SialinkService,
  SiaEventLog,
  SiaReceivedMessage,
  SiaSiteConfig,
  SiaCodeCustom,
} from "../../services/sialink.service";
import { ICAreaService } from "../../services/icarea.service";
import { ICAreaApi } from "../../models/apis/icarea.api";
import { Subscription } from "rxjs";

@Component({
  selector: "app-sialink",
  templateUrl: "./sialink.component.html",
  styleUrls: ["./sialink.component.scss"],
})
export class SialinkComponent implements OnInit, OnDestroy {
  events: SiaEventLog[] = [];
  rawMessages: SiaReceivedMessage[] = [];
  activeTab: "events" | "raw" | "config" = "events";

  // Config Properties
  sites: SiaSiteConfig[] = [];
  selectedSite: SiaSiteConfig = this.getEmptySite();

  isEditingSite: boolean = false;
  areas: ICAreaApi[] = [];

  // Area Selection
  currentAreaLevel = -1;
  IdArea: number;
  AreaIds: number[] = [];
  areaSubscription: Subscription;

  customCodes: SiaCodeCustom[] = [];
  selectedCode: SiaCodeCustom = this.getEmptyCode();
  isEditingCode: boolean = false;
  showCodesForSiteId: number | null = null;

  startDate: string;
  endDate: string;
  siteCode: string;
  excludeInfoTest: boolean = false;

  constructor(
    private sialinkService: SialinkService,
    private areaService: ICAreaService,
  ) {}

  ngOnInit(): void {
    // Default to today
    const now = new Date();
    this.endDate = now.toISOString().slice(0, 16); // format for input datetime-local
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    this.startDate = start.toISOString().slice(0, 16);

    // Initial load will happen after area is resolved in subscription or promise
    // But we should call loadData in case area service is delayed, or we want initial empty state

    this.areaSubscription = this.areaService.CurrentChanged$.subscribe(
      (area: ICAreaApi) => {
        if (area) {
          this.currentAreaLevel = area.Level;
          this.IdArea = area.Id;
        } else {
          this.areaService.getLevel().then((l) => (this.currentAreaLevel = l));
          this.IdArea = null;
        }
        this.loadData();
      },
    );

    this.areaService.current().then((area: ICAreaApi) => {
      if (area) {
        this.currentAreaLevel = area.Level;
        this.IdArea = area.Id;
        this.loadData();
      } else {
        this.areaService.getLevel().then((l) => (this.currentAreaLevel = l));
      }
    });
  }

  ngOnDestroy() {
    if (this.areaSubscription) {
      this.areaSubscription.unsubscribe();
    }
  }

  loadData() {
    if (this.activeTab === "events") {
      this.sialinkService
        .getEvents(
          this.startDate,
          this.endDate,
          this.siteCode,
          this.IdArea,
          this.excludeInfoTest
        )
        .subscribe((data) => (this.events = data));
    } else if (this.activeTab === "raw") {
      this.sialinkService
        .getRawMessages(
          this.startDate,
          this.endDate,
          this.siteCode,
          this.IdArea,
        )
        .subscribe((data) => (this.rawMessages = data));
    } else if (this.activeTab === "config") {
      this.loadSites();
      this.loadAreas();
    }
  }

  loadAreas() {
    this.areaService.list().then((res) => {
      if (res && res.Success) {
        this.areas = res.ListResult;
      }
    });
  }

  loadSites() {
    this.sialinkService
      .getSites(this.IdArea)
      .subscribe((data) => (this.sites = data));
  }

  switchTab(tab: "events" | "raw" | "config") {
    this.activeTab = tab;
    this.loadData();
  }

  // Site Management

  getEmptySite(): SiaSiteConfig {
    return {
      SiteCode: "",
      Enabled: true,
      AcceptedIPs: "",
      SiaServerId: "",
      AreaId: null,
    };
  }

  startAddSite() {
    this.selectedSite = this.getEmptySite();
    this.isEditingSite = true;
    this.showCodesForSiteId = null;
    this.AreaIds = [];
  }

  editSite(site: SiaSiteConfig) {
    this.selectedSite = { ...site }; // Clone
    this.isEditingSite = true;
    this.showCodesForSiteId = null;

    if (this.selectedSite.AreaId) {
      this.AreaIds = [this.selectedSite.AreaId];
    } else {
      this.AreaIds = [];
    }
  }

  cancelEditSite() {
    this.isEditingSite = false;
    this.selectedSite = this.getEmptySite();
  }

  saveSite() {
    if (this.AreaIds && this.AreaIds.length > 0) {
      this.selectedSite.AreaId = this.AreaIds[0];
    } else {
      this.selectedSite.AreaId = null;
    }

    if (this.selectedSite.Id) {
      this.sialinkService.updateSite(this.selectedSite).subscribe(() => {
        this.isEditingSite = false;
        this.loadSites();
      });
    } else {
      this.sialinkService.createSite(this.selectedSite).subscribe(() => {
        this.isEditingSite = false;
        this.loadSites();
      });
    }
  }

  deleteSite(site: SiaSiteConfig) {
    if (confirm(`Confermi la cancellazione del sito ${site.SiteCode}?`)) {
      this.sialinkService
        .deleteSite(site.Id!)
        .subscribe(() => this.loadSites());
    }
  }

  manageCodes(site: SiaSiteConfig) {
    this.showCodesForSiteId = site.Id!;
    this.loadCodes(site.Id!);
    this.isEditingSite = false; // Close site editor if open
  }

  // Code Management

  getEmptyCode(): SiaCodeCustom {
    return {
      SiteConfigId: this.showCodesForSiteId!,
      EventCode: "",
      EventValue: "",
      Description: "",
    };
  }

  loadCodes(siteId: number) {
    this.sialinkService
      .getCustomCodes(siteId)
      .subscribe((data) => (this.customCodes = data));
  }

  startAddCode() {
    this.selectedCode = this.getEmptyCode();
    this.isEditingCode = true;
  }

  editCode(code: SiaCodeCustom) {
    this.selectedCode = { ...code };
    this.isEditingCode = true;
  }

  cancelEditCode() {
    this.isEditingCode = false;
  }

  saveCode() {
    if (this.selectedCode.Id) {
      this.sialinkService.updateCustomCode(this.selectedCode).subscribe(() => {
        this.isEditingCode = false;
        this.loadCodes(this.showCodesForSiteId!);
      });
    } else {
      this.sialinkService.createCustomCode(this.selectedCode).subscribe(() => {
        this.isEditingCode = false;
        this.loadCodes(this.showCodesForSiteId!);
      });
    }
  }

  deleteCode(code: SiaCodeCustom) {
    if (confirm(`Delete code ${code.EventCode}?`)) {
      this.sialinkService
        .deleteCustomCode(code.Id!)
        .subscribe(() => this.loadCodes(this.showCodesForSiteId!));
    }
  }

  getBadgeClass(eventType: string): string {
    switch (eventType) {
      case "INFO":
        return "badge-success";
      case "ALARM":
        return "badge-danger";
      case "WARNING":
        return "badge-warning";
      default:
        return "badge-secondary";
    }
  }
}
